/**
 * UserManagementTable — Table for managing user accounts.
 * Displays user info with role selection and enable/disable toggle.
 * Supports optimistic updates, error display, empty state, and responsive scrolling.
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7
 */

import React, { useCallback } from 'react';
import type { AdminUser } from '../../state/admin-context';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserManagementTableProps {
  readonly users: AdminUser[];
  readonly onUpdateUser: (
    userId: string,
    updates: { role?: 'admin' | 'user'; enabled?: boolean },
  ) => Promise<void>;
  readonly error: string | null;
  readonly onRetry?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UserManagementTable({
  users,
  onUpdateUser,
  error,
  onRetry,
}: UserManagementTableProps): React.ReactElement {
  // Load failure: error with no users shows error alert + retry
  if (error && users.length === 0) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
        {onRetry && (
          <Button
            type="button"
            className="mt-2"
            onClick={onRetry}
          >
            Retry
          </Button>
        )}
      </Alert>
    );
  }

  // Empty state: no users registered
  if (users.length === 0) {
    return (
      <Alert variant="info" role="status">
        <AlertDescription>No users are registered</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      {/* Error alert for update failures (users still visible) */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Table with built-in responsive scroll wrapper */}
      <Table aria-label="User management">
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Display Name</TableHead>
            <TableHead scope="col">Email</TableHead>
            <TableHead scope="col">Role</TableHead>
            <TableHead scope="col">Status</TableHead>
            <TableHead scope="col">Last Login</TableHead>
            <TableHead scope="col"><span className="sr-only">Actions</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <UserRow key={user.userId} user={user} onUpdateUser={onUpdateUser} />
          ))}
        </TableBody>
      </Table>
    </>
  );
}

// ---------------------------------------------------------------------------
// User row
// ---------------------------------------------------------------------------

interface UserRowProps {
  readonly user: AdminUser;
  readonly onUpdateUser: (
    userId: string,
    updates: { role?: 'admin' | 'user'; enabled?: boolean },
  ) => Promise<void>;
}

function UserRow({ user, onUpdateUser }: UserRowProps): React.ReactElement {
  const handleRoleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newRole = e.target.value as 'admin' | 'user';
      onUpdateUser(user.userId, { role: newRole });
    },
    [user.userId, onUpdateUser],
  );

  const handleToggleEnabled = useCallback(() => {
    onUpdateUser(user.userId, { enabled: !user.enabled });
  }, [user.userId, user.enabled, onUpdateUser]);

  const formattedLastLogin = user.lastLogin
    ? new Date(user.lastLogin).toLocaleDateString()
    : 'Never';

  return (
    <TableRow>
      <TableCell>{user.displayName}</TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>
        <select
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          value={user.role}
          onChange={handleRoleChange}
          aria-label={`Role for ${user.displayName}`}
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
      </TableCell>
      <TableCell>
        <span className={user.enabled ? 'text-success' : 'text-destructive'}>
          {user.enabled ? 'Active' : 'Disabled'}
        </span>
      </TableCell>
      <TableCell>{formattedLastLogin}</TableCell>
      <TableCell>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleToggleEnabled}
          aria-label={user.enabled ? `Disable ${user.displayName}` : `Enable ${user.displayName}`}
        >
          {user.enabled ? 'Disable' : 'Enable'}
        </Button>
      </TableCell>
    </TableRow>
  );
}
